<h2 lvrPadding [padHorizontal]="32" [padVertical]="0">{{ 'lek.create.title' | transloco }}</h2>
<form class="form-container" [formGroup]="form" lvrAutoSave="lek" [entityId]="draftId" [autoLoad]="false">
	<section class="form-grid">
		<lvr-field [label]="'lek.create.p3start' | transloco">
			<p>{{ phase3Start() ? (phase3Start() | date: DATEFORMAT) : '' }}</p>
		</lvr-field>

		<div>
			<lvr-field [label]="'lek.create.occupationTitle' | transloco">
				<p>
					{{
						lekCategories()?.institutionBranch?.workplaceJobs?.length! > 0
							? lekCategories()!.institutionBranch!.workplaceJobs[0].occupationTitle
							: '-'
					}}
				</p>
			</lvr-field>
		</div>

		<!--Startdatum: max. -42d, nach P3, nach Verschiebbericht, nicht in Zukunft-->
		<lvr-field [label]="'lek.create.startDate' | transloco | appendAsterisk">
			@let startDateCtrl = form.get('startDate');
			<mat-form-field lvrInput [lvrError]="hasError(startDateCtrl, 'required', 'minDateExceeded', 'maxDateExceeded')">
				<input
					matInput
					formControlName="startDate"
					[matDatepicker]="startPicker"
					placeholder="10.10.2025"
					[min]="validMinDate()"
					[max]="validMaxDate()"
					[errorStateMatcher]="matcher"
				/>
				@if (hasError(startDateCtrl, 'required')) {
					<mat-error> {{ 'lek.create.error.required' | transloco }} </mat-error>
				}
				@if (hasError(startDateCtrl, 'minDateExceeded')) {
					<mat-error> {{ 'lek.create.error.minDateExceeded' | transloco }} </mat-error>
				}
				@if (hasError(startDateCtrl, 'maxDateExceeded')) {
					<mat-error> {{ 'lek.create.error.maxDateExceeded' | transloco }} </mat-error>
				}
				<mat-datepicker-toggle matIconSuffix [for]="startPicker"></mat-datepicker-toggle>
				<mat-datepicker #startPicker></mat-datepicker>
			</mat-form-field>
		</lvr-field>

		<!--Enddatum: max -42d, nicht in Zukunft, EndDatum >= StartDatum-->
		<lvr-field [label]="'lek.create.endDate' | transloco">
			@let endDateCtrl = form.get('endDate');
			<mat-form-field
				lvrInput
				[lvrError]="hasError(endDateCtrl, 'minDateExceeded', 'maxDateExceeded', 'endBeforeStart')"
			>
				<input
					matInput
					formControlName="endDate"
					[matDatepicker]="endPicker"
					placeholder="10.10.2025"
					[min]="validMinDate()"
					[max]="validMaxDate()"
					[errorStateMatcher]="matcher"
				/>
				@if (hasError(endDateCtrl, 'minDateExceeded')) {
					<mat-error> {{ 'lek.create.error.minDateExceeded' | transloco }} </mat-error>
				}
				@if (hasError(endDateCtrl, 'maxDateExceeded')) {
					<mat-error> {{ 'lek.create.error.maxDateExceeded' | transloco }} </mat-error>
				}
				@if (hasError(endDateCtrl, 'endBeforeStart')) {
					<mat-error>{{ 'base.form.error.end_date_error' | transloco }}</mat-error>
				}
				<mat-datepicker-toggle matIconSuffix [for]="endPicker"></mat-datepicker-toggle>
				<mat-datepicker #endPicker></mat-datepicker>
			</mat-form-field>
		</lvr-field>
		<!-- Result-->
		@let resultCtrl = form.get('result');
		<lvr-field [label]="'lek.create.result' | transloco">
			<mat-form-field lvrInput [lvrError]="hasError(resultCtrl, 'maxLength')">
				<textarea
					matInput
					formControlName="result"
					placeholder="Ergebnis"
					[maxLength]="env.formConfiguration.lek.resultLength"
					[errorStateMatcher]="matcher"
				></textarea>
				@if (hasError(resultCtrl, 'maxLength')) {
					<mat-error> {{ 'lek.create.error.maxLength' | transloco }}</mat-error>
				}
			</mat-form-field>
		</lvr-field>

		<!-- Leistung / Ergebnis-->
		@let performanceResultCtrl = form.get('performanceResultId');
		<lvr-field [label]="'lek.create.performanceResult' | transloco | appendAsterisk">
			<mat-form-field lvrInput [lvrError]="hasError(performanceResultCtrl, 'required')">
				<mat-select
					formControlName="performanceResultId"
					[placeholder]="'base.field.select_please' | transloco"
					aria-required="true"
				>
					@for (option of performanceResultOptions(); track option.id) {
						<mat-option [value]="option.id" [aria-label]="'Ausdifferenzierung ' + option.description">
							<span class="differentiation-option-content">
								<span>{{ option.description }}</span>
								@if (hasHintText(option.id) | async) {
									<lvr-info-tooltip
										class="differentiation-info-icon"
										[text]="getInfoTextKey(option.id) | transloco"
										[ariaLabel]="infoTextAriaLabel(option.description)"
									/>
								}
							</span>
						</mat-option>
					}
				</mat-select>
				@if (hasError(performanceResultCtrl, 'required')) {
					<mat-error>{{ 'lek.create.error.performanceResult_required' | transloco }}</mat-error>
				}
			</mat-form-field>
		</lvr-field>
	</section>

	@switch (selectedPerformanceResult()?.requiredSponsor) {
		@case ('S') {
			<div [formGroup]="additionalFieldsGroup">
				<!-- Zweigstelle (Schule) -->
				<lvr-field [label]="'lek.create.school' | transloco">
					<p lvrPadding [padHorizontal]="8" [padVertical]="0">{{ preSelectedInstitutionBranch()?.name ?? '-' }}</p>
					@if (!preSelectedInstitutionBranch()) {
						<mat-hint>{{ 'lek.create.hint_school' | transloco }}</mat-hint>
					}
				</lvr-field>
			</div>
		}
		@case ('J') {
			<div [formGroup]="additionalFieldsGroup" class="form-grid">
				<!-- Zweigstelle -->
				<lvr-field [label]="'lek.create.workplace' | transloco">
					<p lvrPadding [padHorizontal]="8" [padVertical]="0">{{ preSelectedInstitutionBranch()?.name ?? '-' }}</p>
					@if (!preSelectedInstitutionBranch()) {
						<mat-hint>{{ 'lek.create.hint_workplace' | transloco }}</mat-hint>
					}
				</lvr-field>

				<!-- Leistungsträger -->
				@let sponsorCtrl = additionalFieldsGroup.get('sponsorId');
				<lvr-field [label]="'lek.create.sponsor' | transloco | appendAsterisk">
					<mat-form-field lvrInput [lvrError]="hasError(sponsorCtrl, 'required')">
						<mat-select formControlName="sponsorId" placeholder="Leistungsträger" aria-required="true">
							@for (option of sponsors(); track option.id) {
								<mat-option [value]="option.id">
									{{ option.description }}
								</mat-option>
							}
						</mat-select>
						@if (hasError(sponsorCtrl, 'required')) {
							<mat-error>{{ 'lek.create.error.sponsor_required' | transloco }}</mat-error>
						}
					</mat-form-field>
				</lvr-field>
			</div>
		}
		@case ('X') {
			<div [formGroup]="additionalFieldsGroup">
				<lvr-field [label]="'lek.create.sponsor' | transloco">
					<mat-form-field lvrInput>
						<mat-select formControlName="sponsorId" placeholder="Leistungsträger">
							@for (option of sponsors(); track option.id) {
								<mat-option [value]="option.id">
									{{ option.description }}
								</mat-option>
							}
						</mat-select>
					</mat-form-field>
				</lvr-field>
			</div>
		}
		@case ('E') {
			<!-- No additional fields -->
		}
		@case ('T') {
			<lvr-field [label]="'lek.create.extern_or_intern' | transloco" lvrPadding [padVertical]="16" [padHorizontal]="0">
				<div [formGroup]="additionalFieldsGroup">
					<mat-checkbox formControlName="isHandledByExternalSponsor">
						{{ 'lek.create.external_handled' | transloco }}
					</mat-checkbox>
				</div>
			</lvr-field>
		}
		@case ('N') {
			<!-- No additional fields -->
		}
		@default {}
	}

	@if (canEditDuration()) {
		<div class="form-grid">
			@let durationCtrl = form.get('duration');
			<lvr-field [label]="'lek.create.duration' | transloco | appendAsterisk">
				<mat-form-field lvrInput [lvrError]="hasError(durationCtrl, 'required', 'min', 'max')">
					<input
						matInput
						type="number"
						formControlName="duration"
						placeholder="'1, 2, 3"
						aria-required="true"
						min="1"
						max="3"
						step="1"
					/>
					@if (hasError(durationCtrl, 'required')) {
						<mat-error>{{ 'lek.create.error.required' | transloco }}</mat-error>
					} @else if (hasError(durationCtrl, 'min')) {
						<mat-error>{{ 'lek.create.error.min' | transloco: { min: 1 } }}</mat-error>
					} @else if (hasError(durationCtrl, 'max')) {
						<mat-error>{{ 'lek.create.error.max' | transloco: { max: 3 } }}</mat-error>
					}
				</mat-form-field>
			</lvr-field>
		</div>
	}

	<p lvrPadding [padHorizontal]="0" [padVertical]="24">{{ 'base.form.required' | transloco }}</p>

	<lvr-default-actions
		[disableSave]="form.invalid || !canCreateLekPermission()"
		(save)="save()"
		(cancelSave)="cancel()"
		[showLoading]="isLoading()"
	>
		<lvr-restore-actions entityType="lek" [entityId]="draftId"></lvr-restore-actions>
	</lvr-default-actions>
</form>
